import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useRegion } from '@/hooks/useRegion'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { useDistrictsByRegion } from '@/hooks/useDistrictsByRegion'
import { AppLayout } from '@/components/layout/AppLayout'
import { AdminOnly } from '@/components/AdminOnly'
import { DistrictCard } from '@/components/district/DistrictCard'
import { DistrictCardSkeleton } from '@/components/district/DistrictCardSkeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { RegionType } from '@/types'

const regionTypeLabels: Record<RegionType, string> = {
  coastal: 'Coastal',
  mountain: 'Mountain',
  plains: 'Plains',
  forest: 'Forest',
  urban_core: 'Urban Core',
}

const regionTypeBadgeStyles: Record<RegionType, string> = {
  coastal: 'bg-blue-100 text-blue-800 border-blue-200',
  mountain: 'bg-stone-100 text-stone-800 border-stone-200',
  plains: 'bg-amber-100 text-amber-800 border-amber-200',
  forest: 'bg-green-100 text-green-800 border-green-200',
  urban_core: 'bg-purple-100 text-purple-800 border-purple-200',
}

function formatPopulation(population: number): string {
  return population.toLocaleString()
}

export default function RegionDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { region, isLoading: regionLoading, error: regionError } = useRegion(id)
  const { nation, isLoading: nationLoading } = useNation(region?.nation_id)
  const { world, isLoading: worldLoading } = useWorld(nation?.world_id)
  const { districts, isLoading: districtsLoading, error: districtsError } = useDistrictsByRegion(id)

  const isLoading = regionLoading
  const error = regionError
  const isParentLoading = nationLoading || worldLoading

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name, href: `/nations/${nation.id}` })
    }
    if (region) {
      items.push({ label: region.name })
    }
    return items
  }, [world, nation, region])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {regionError?.message === 'JSON object requested, multiple (or no) rows returned'
            ? 'Region not found.'
            : 'Failed to load region. Please try again later.'}
        </div>
      )}

      {(isLoading || isParentLoading) && (
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="bg-muted h-8 w-48 animate-pulse rounded" />
            <div className="bg-muted h-6 w-20 animate-pulse rounded-full" />
          </div>
          <Card>
            <CardHeader>
              <div className="bg-muted h-5 w-32 animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-muted h-4 w-40 animate-pulse rounded" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {!isLoading && !isParentLoading && !error && region && (
        <>
          <div className="mb-8 flex items-center gap-3">
            <h1 className="text-3xl font-bold">{region.name}</h1>
            <Badge variant="outline" className={cn(regionTypeBadgeStyles[region.region_type])}>
              {regionTypeLabels[region.region_type]}
            </Badge>
          </div>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Region Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Type</dt>
                  <dd className="font-medium">{regionTypeLabels[region.region_type]}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Seat Count</dt>
                  <dd className="font-medium">{region.seat_count}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Population</dt>
                  <dd className="font-medium">{formatPopulation(region.population)}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          <div className="border-t pt-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Districts</h2>
              <AdminOnly>
                <Button onClick={() => navigate(`/regions/${id}/districts/new`)}>
                  Add District
                </Button>
              </AdminOnly>
            </div>

            {districtsError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
                Failed to load districts. Please try again later.
              </div>
            )}

            {districtsLoading && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <DistrictCardSkeleton key={i} />
                ))}
              </div>
            )}

            {!districtsLoading && !districtsError && districts && districts.length === 0 && (
              <div className="rounded-lg border border-dashed p-12 text-center">
                <h3 className="text-lg font-medium">No districts yet</h3>
                <p className="text-muted-foreground mt-1">
                  This region doesn't have any districts yet.
                </p>
                <AdminOnly>
                  <Button className="mt-4" onClick={() => navigate(`/regions/${id}/districts/new`)}>
                    Create the first district
                  </Button>
                </AdminOnly>
              </div>
            )}

            {!districtsLoading && !districtsError && districts && districts.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {districts.map((district) => (
                  <DistrictCard key={district.id} district={district} regionType={region.region_type} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </AppLayout>
  )
}
